import { Box, Paper, Slider, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { useThemeMode } from '../theme'
import {
  ArtCanvasIcon,
  BartenderIcon,
  BeerCheersIcon,
  BeerGripIcon,
  CheckInDeskIcon,
  ChecklistIcon,
  TearoomIcon,
  ConMapIcon,
  ConventionCrowdIcon,
  CrouchingWolfIcon,
  FursuitDance2Icon,
  FursuitDanceIcon,
  FursuitParadeIcon,
  GameShowIcon,
  GroupPhoto3Icon,
  GroupPhotoIcon,
  ParkingIcon,
  PhotoboothIcon,
  ReachingIcon,
  RegistrationTerminalIcon,
  SnackBowlIcon,
  StreamIcon,
  TakeMyMoneyIcon,
  VotingIcon,
  WolfPresentationIcon,
  WolfSilhouetteIcon
} from './components/widgets'

const customIcons = [
  { name: 'ArtCanvasIcon', Icon: ArtCanvasIcon },
  { name: 'BartenderIcon', Icon: BartenderIcon },
  { name: 'BeerCheersIcon', Icon: BeerCheersIcon },
  { name: 'BeerGripIcon', Icon: BeerGripIcon },
  { name: 'CheckInDeskIcon', Icon: CheckInDeskIcon },
  { name: 'ChecklistIcon', Icon: ChecklistIcon },
  { name: 'TearoomIcon', Icon: TearoomIcon },
  { name: 'ConMapIcon', Icon: ConMapIcon },
  { name: 'ConventionCrowdIcon', Icon: ConventionCrowdIcon },
  { name: 'CrouchingWolfIcon', Icon: CrouchingWolfIcon },
  { name: 'FursuitDance2Icon', Icon: FursuitDance2Icon },
  { name: 'FursuitDanceIcon', Icon: FursuitDanceIcon },
  { name: 'FursuitParadeIcon', Icon: FursuitParadeIcon },
  { name: 'GameShowIcon', Icon: GameShowIcon },
  { name: 'GroupPhotoIcon', Icon: GroupPhotoIcon },
  { name: 'GroupPhoto3Icon', Icon: GroupPhoto3Icon },
  { name: 'ParkingIcon', Icon: ParkingIcon },
  { name: 'PhotoboothIcon', Icon: PhotoboothIcon },
  { name: 'ReachingIcon', Icon: ReachingIcon },
  { name: 'RegistrationTerminalIcon', Icon: RegistrationTerminalIcon },
  { name: 'SnackBowlIcon', Icon: SnackBowlIcon },
  { name: 'StreamIcon', Icon: StreamIcon },
  { name: 'TakeMyMoneyIcon', Icon: TakeMyMoneyIcon },
  { name: 'VotingIcon', Icon: VotingIcon },
  { name: 'WolfPresentationIcon', Icon: WolfPresentationIcon },
  { name: 'WolfSilhouetteIcon', Icon: WolfSilhouetteIcon }
]

const ICON_STYLES = {
  ios: {
    label: 'iOS',
    radius: '18px',
    shadow: '0 6px 20px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.3) inset',
    border: '1px solid rgba(255,255,255,0.18)',
    fontSize: 52,
    overlays: (
      <>
        <Box sx={{ position: 'absolute', top: '-10%', left: '5%', right: '5%', height: '52%', background: 'linear-gradient(180deg, rgba(255,255,255,0.52) 0%, rgba(255,255,255,0.18) 60%, rgba(255,255,255,0) 100%)', borderRadius: '0 0 50% 50%', pointerEvents: 'none', zIndex: 2 }} />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', background: 'linear-gradient(0deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0) 100%)', pointerEvents: 'none', zIndex: 2 }} />
      </>
    )
  },
  macos: {
    label: 'macOS',
    radius: '20px',
    shadow: '0 10px 40px rgba(0,0,0,0.45), 0 2px 0 rgba(255,255,255,0.45) inset, 0 -1px 0 rgba(0,0,0,0.35) inset',
    border: '1px solid rgba(255,255,255,0.28)',
    fontSize: 48,
    overlays: (
      <>
        {/* Wide top specular */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '44%', background: 'linear-gradient(180deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.28) 50%, rgba(255,255,255,0) 100%)', borderRadius: '20px 20px 60% 60%', pointerEvents: 'none', zIndex: 2 }} />
        {/* Left rim highlight */}
        <Box sx={{ position: 'absolute', top: '8%', bottom: '8%', left: 0, width: 2, background: 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.15) 70%, rgba(255,255,255,0) 100%)', pointerEvents: 'none', zIndex: 3 }} />
        {/* Frosted mid-layer */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(145deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 55%, rgba(0,0,0,0.1) 100%)', pointerEvents: 'none', zIndex: 2 }} />
        {/* Bottom depth */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: 'linear-gradient(0deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0) 100%)', pointerEvents: 'none', zIndex: 2 }} />
      </>
    )
  },
  material: {
    label: 'Material',
    radius: '22px',
    shadow: '0 1px 3px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.12)',
    border: 'none',
    fontSize: 58,
    overlays: (
      <>
        {/* Subtle tonal wash */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(0,0,0,0.06) 100%)', pointerEvents: 'none', zIndex: 2 }} />
      </>
    )
  }
}

const AppIcon = ({ Icon, name, headerColor, paperColor, iconOpacity = 1, iconStyle = 'ios' }) => {
  const style = ICON_STYLES[iconStyle]
  return (
    <Tooltip title={name} placement="top" arrow>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 72, height: 72, borderRadius: style.radius, backgroundColor: headerColor, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', boxShadow: style.shadow, border: style.border }}>
          <Icon sx={{ color: paperColor, fontSize: style.fontSize, position: 'relative', zIndex: 1, opacity: iconOpacity }} />
          {style.overlays}
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', textAlign: 'center', lineHeight: 1.2 }}>
          {name}
        </Typography>
      </Box>
    </Tooltip>
  )
}

const AppIconSection = ({ title, headerColor, paperColor, iconStyle }) => {
  const [opacity, setOpacity] = useState(1)

  return (
    <Paper elevation={2} sx={{ maxWidth: '600px', margin: '0 auto', px: 2, pt: 2, pb: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>{title}</Typography>
        <Slider size="small" min={0.1} max={1} step={0.01} value={opacity} onChange={(_, v) => setOpacity(v)} sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', minWidth: 32 }}>
          {Math.round(opacity * 100)}%
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 2 }}>
        {customIcons.map(({ name, Icon }) => (
          <AppIcon key={name} Icon={Icon} name={name} headerColor={headerColor} paperColor={paperColor} iconOpacity={opacity} iconStyle={iconStyle} />
        ))}
      </Box>
    </Paper>
  )
}

export const WidgetsIcons = () => {
  const { palette } = useThemeMode()
  const [iconStyle, setIconStyle] = useState('ios')

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Paper elevation={2} sx={{ maxWidth: '600px', margin: '0 auto', px: 2, pt: 2, pb: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2">Icon Style</Typography>
        <ToggleButtonGroup size="small" exclusive value={iconStyle} onChange={(_, v) => v && setIconStyle(v)}>
          {Object.entries(ICON_STYLES).map(([key, { label }]) => (
            <ToggleButton key={key} value={key}>{label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      <Paper elevation={2} sx={{ maxWidth: '600px', margin: '0 auto', px: 2, pt: 2, pb: 1, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Custom Icons</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {customIcons.map(({ name, Icon }) => (
            <Tooltip key={name} title={name} placement="top" arrow>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Icon sx={{ fontSize: 48, color: palette.headerColor }} />
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{name}</Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Paper>

      <AppIconSection title="App Icons (Font)" headerColor={palette.headerColor} paperColor={palette.textColor} iconStyle={iconStyle} />
      <AppIconSection title="App Icons" headerColor={palette.headerColor} paperColor={palette.paperColor} iconStyle={iconStyle} />
      <AppIconSection title="App Icons (Inverted)" headerColor={palette.paperColor} paperColor={palette.headerColor} iconStyle={iconStyle} />
      <AppIconSection title="App Icons (Dim)" headerColor={palette.headerColor} paperColor={palette.headerDimColor} iconStyle={iconStyle} />
      <AppIconSection title="App Icons (Bright)" headerColor={palette.headerColor} paperColor={palette.headerBrightColor} iconStyle={iconStyle} />
    </Box>
  )
}
